import { FC, KeyboardEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useData } from "data/data-provider";
import DbIcon from "designs/DesignB/components/icons";
import { isNotBookable, isPriceOnRequest } from "utils/carPrice";
import { Feature, SpecGrid, EMPTY_DETAILS } from "./CarDetailsBody";
import { CarCta, CarPrice } from "./CarCta";
import SwipeGallery from "./SwipeGallery";
import { DetailSpec, descriptionText, detailSpecs, featureNames, galleryPhotos } from "./carDetails";

const reducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Calls `fn` whenever the element changes size. No-op without ResizeObserver. */
const useResize = (ref: { current: HTMLElement | null }, fn: () => void) => {
  const latest = useRef(fn);
  latest.current = fn;
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // The size it was measured at: the observer's first call is no change.
    const r = el.getBoundingClientRect();
    let w = r.width;
    let h = r.height;
    const ro = new ResizeObserver(([e]) => {
      const { width, height } = e.contentRect;
      if (width === w && height === h) return;
      w = width;
      h = height;
      latest.current();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
};

/**
 * A list that shows only what fits in the room it is given, then "+N more". Measured before
 * paint: every item, then as many as fit with the button in place of the last one.
 */
const FitList: FC<{ count: number; className: string; label: string; onMore: () => void; children: ReactNode[] }> = ({
  count,
  className,
  label,
  onMore,
  children,
}) => {
  const ref = useRef<HTMLUListElement>(null);
  const [fit, setFit] = useState(count);
  const [phase, setPhase] = useState<"measure" | "check" | "done">("measure");
  useLayoutEffect(() => setPhase("measure"), [count]);
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box || phase === "done") return;
    const room = box.clientHeight;
    if (phase === "measure") {
      // No layout (a test, a hidden tab): everything.
      if (!room) {
        setFit(count);
        setPhase("done");
        return;
      }
      const items = Array.from(box.children) as HTMLElement[];
      const over = items.findIndex((el) => el.offsetTop + el.offsetHeight > room + 1);
      setFit(over === -1 ? count : Math.max(0, over - 1));
      setPhase("check");
    } else if (box.scrollHeight > room + 1 && fit > 0) {
      setFit(fit - 1);
    } else {
      setPhase("done");
    }
  });
  useResize(ref, () => setPhase("measure"));
  const shown = phase === "measure" ? count : fit;
  return (
    <ul ref={ref} className={`db-fit ${className}`} aria-label={label}>
      {children.slice(0, shown)}
      {shown < count && (
        <li>
          <button type="button" className="db-xp-more" onClick={onMore}>
            +{count - shown} more
          </button>
        </li>
      )}
    </ul>
  );
};

/** The description in whatever height is left, faded at the cut, with Read more when it is cut. */
const FitText: FC<{ text: string; onMore: () => void }> = ({ text, onMore }) => {
  const box = useRef<HTMLDivElement>(null);
  const p = useRef<HTMLParagraphElement>(null);
  const [maxH, setMaxH] = useState<number | null>(null);
  const [cut, setCut] = useState(false);
  const measure = () => {
    const b = box.current;
    const el = p.current;
    if (!b || !el || !b.clientHeight) return;
    const lh = parseFloat(getComputedStyle(el).lineHeight) || 22;
    const h = Math.max(1, Math.floor(b.clientHeight / lh)) * lh;
    setMaxH(h);
    setCut(el.scrollHeight > h + 1);
  };
  useLayoutEffect(measure, [text]);
  useResize(box, measure);
  return (
    <>
      <div ref={box} className="db-sheet__descbox">
        <p ref={p} className={`db-sheet__desc ${cut ? "is-cut" : ""}`} style={maxH ? { maxHeight: maxH } : undefined}>
          {text}
        </p>
      </div>
      {cut && (
        <button type="button" className="db-xp-link db-sheet__readmore" onClick={onMore}>
          Read more
        </button>
      )}
    </>
  );
};

/** "5 seats", "Automatic", "Driver 21+": a spec as one short chip. */
const chipText = (s: DetailSpec) =>
  s.key === "seats" || s.key === "doors"
    ? `${s.value} ${s.label.toLowerCase()}`
    : s.key === "age"
    ? `Driver ${s.value}`
    : s.value;

type Tab = "overview" | "specs" | "features";
type More = "description" | "specs" | "features";

const TAB_LABEL: Record<Tab, string> = { overview: "Overview", specs: "Specs", features: "Features" };
const MORE_LABEL: Record<More, string> = { description: "About this car", specs: "Specs", features: "Features" };

export interface CarSheetProps {
  car: any;
  titleId: string;
  /** Set when the sheet should slide away; it calls onGone once it has. */
  closing: boolean;
  /** Opened by a tap (not by the URL): the sheet gets a history entry, so Back closes it. */
  ownHistory: boolean;
  onClose: () => void;
  onGone: () => void;
  /** None on the car page, where the renter is already booking: the sheet has no foot there. */
  onBook?: () => void;
}

/**
 * The car's details on a phone (1NOW-11219): a full-screen sheet that always fits the screen, so
 * nothing on it scrolls. Top to bottom: the photos (a fixed share of the height) with the close
 * button, the name and the daily price, the tabs (Overview, Specs, Features) and Book now pinned
 * at the foot. What does not fit in a tab (a long description, many features) opens a Read more
 * view inside the sheet, and only that view's text scrolls. Escape, the close button or the
 * phone's Back closes it.
 */
const CarSheet: FC<CarSheetProps> = ({ car, titleId, closing, ownHistory, onClose, onGone, onBook }) => {
  const { ourCompany } = useData();
  const sheet = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(reducedMotion());
  const [tab, setTab] = useState<Tab>("overview");
  const [more, setMore] = useState<More | null>(null);

  const specs = detailSpecs(car, ourCompany);
  const description = descriptionText(car?.description);
  const features = featureNames(car);
  const tabs: Tab[] = ["overview", ...(specs.length ? ["specs" as Tab] : []), ...(features.length ? ["features" as Tab] : [])];
  const chips = specs.slice(0, 4);

  // Slide in: the closed position is drawn first, then the class moves it.
  useLayoutEffect(() => {
    void sheet.current?.offsetHeight;
    setShown(true);
    sheet.current?.focus({ preventScroll: true });
  }, []);

  // The page behind does not scroll while the sheet is up.
  useEffect(() => {
    document.documentElement.classList.add("db-sheet-open");
    return () => document.documentElement.classList.remove("db-sheet-open");
  }, []);

  // Back closes the sheet. A tap pushes an entry of the page's own URL for Back to take off.
  const pushed = useRef(false);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (ownHistory && !pushed.current) {
      window.history.pushState(window.history.state, "", window.location.href);
      pushed.current = true;
    }
    const onPop = () => {
      pushed.current = false;
      close.current();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** Close from inside: through Back when the sheet has its own entry, so the entry goes too. */
  const dismiss = () => {
    if (pushed.current) window.history.back();
    else onClose();
  };

  // Slide away, then go. transitionend, with a fallback for a tab that never paints it.
  useEffect(() => {
    if (!closing) return;
    if (reducedMotion()) {
      onGone();
      return;
    }
    const t = setTimeout(onGone, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (more) setMore(null);
      else dismiss();
      return;
    }
    if (e.key !== "Tab" || !sheet.current) return;
    // Keep Tab inside the sheet.
    const focusable = Array.from(
      sheet.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'),
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === sheet.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = tabs.indexOf(tab);
    const next =
      e.key === "ArrowRight" ? tabs[(i + 1) % tabs.length] : e.key === "ArrowLeft" ? tabs[(i - 1 + tabs.length) % tabs.length] : null;
    if (!next) return;
    e.preventDefault();
    setTab(next);
    document.getElementById(`${titleId}-tab-${next}`)?.focus();
  };

  const overview = (
    <>
      {chips.length > 0 && (
        <ul className="db-sheet__chips" aria-label="Key specs">
          {chips.map((s) => (
            <li key={s.key} className="db-spec">
              <span className="db-spec__icon">
                <DbIcon name={s.icon} />
              </span>
              <span className="min-w-0 truncate">{chipText(s)}</span>
            </li>
          ))}
        </ul>
      )}
      {description ? (
        <FitText text={description} onMore={() => setMore("description")} />
      ) : features.length ? (
        <FitList count={features.length} className="db-xp-feats" label="Features" onMore={() => setMore("features")}>
          {features.map((f) => (
            <Feature key={f} name={f} />
          ))}
        </FitList>
      ) : (
        !chips.length && <p className="db-xp-empty">{EMPTY_DETAILS}</p>
      )}
    </>
  );

  const panes: Record<Tab, ReactNode> = {
    overview,
    specs: (
      <FitList count={specs.length} className="db-xp-specs" label="Specs" onMore={() => setMore("specs")}>
        {specs.map((s) => (
          <li key={s.key} className="db-xp-spec">
            <DbIcon name={s.icon} className="db-xp-spec__icon" />
            <b>{s.value}</b>
            <span>{s.label}</span>
          </li>
        ))}
      </FitList>
    ),
    features: (
      <FitList count={features.length} className="db-xp-feats" label="Features" onMore={() => setMore("features")}>
        {features.map((f) => (
          <Feature key={f} name={f} />
        ))}
      </FitList>
    ),
  };

  const bookable = !isNotBookable(car);

  return createPortal(
    <div className="design-b">
      <div
        ref={sheet}
        className={`db-sheet ${shown && !closing ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        onTransitionEnd={(e) => {
          if (closing && e.target === sheet.current && e.propertyName === "transform") onGone();
        }}
      >
        <div className="db-sheet__main">
          <div className="db-sheet__gal">
            <SwipeGallery photos={galleryPhotos(car)} name={car?.name ?? "this car"} />
            <button type="button" className="db-sheet__x" aria-label="Close details" onClick={dismiss}>
              <DbIcon name="close" className="h-[22px] w-[22px]" />
            </button>
          </div>
          <div className="db-sheet__head">
            <h2 className="db-sheet__name" id={titleId} title={car?.name}>
              {car?.name}
            </h2>
            <CarPrice car={car} />
          </div>
          {tabs.length > 1 && (
            <div className="db-sheet__tabs" role="tablist" aria-label="Car details">
              {tabs.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  id={`${titleId}-tab-${t}`}
                  aria-selected={tab === t}
                  aria-controls={`${titleId}-pane`}
                  tabIndex={tab === t ? 0 : -1}
                  className="db-sheet__tab"
                  onClick={() => setTab(t)}
                  onKeyDown={onTabKey}
                >
                  {TAB_LABEL[t]}
                </button>
              ))}
            </div>
          )}
          <div
            className="db-sheet__body"
            id={`${titleId}-pane`}
            role={tabs.length > 1 ? "tabpanel" : undefined}
            aria-labelledby={tabs.length > 1 ? `${titleId}-tab-${tab}` : undefined}
            key={tab}
          >
            {panes[tab]}
          </div>
          {more && (
            <div className="db-sheet__more" role="region" aria-label={MORE_LABEL[more]}>
              <div className="db-sheet__morebar">
                <button
                  type="button"
                  className="db-xp-x"
                  aria-label="Back to the car"
                  autoFocus
                  onClick={() => setMore(null)}
                >
                  <DbIcon name="arrowLeft" className="h-[22px] w-[22px]" />
                </button>
                <h3>{MORE_LABEL[more]}</h3>
              </div>
              <div className="db-sheet__morebody" tabIndex={0}>
                {more === "description" && <p className="db-xp-desc">{description}</p>}
                {more === "specs" && <SpecGrid specs={specs} />}
                {more === "features" && (
                  <ul className="db-xp-feats" aria-label="Features">
                    {features.map((f) => (
                      <Feature key={f} name={f} />
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
        {onBook && (bookable || isPriceOnRequest(car)) && (
          <div className="db-sheet__foot">
            <CarCta car={car} onBook={onBook} />
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default CarSheet;
